/**
 * VireoHR - Super Admin System Settings
 * Styled with Gosta design system
 * Features: Global settings, Announcements, Push notifications, System stats
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Input from '@/components/Input';
import Layout from '@/components/Layout';
import {
  updateSystemPricing,
  updateGlobalSettings,
  sendSystemAnnouncement,
  sendAdminPushNotification
} from '@/services/cloudFunctions';


export default function SystemSettings() {
  const [announcement, setAnnouncement] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [sending, setSending] = useState(false);

  // PRD v4.1: Expected Monthly Hours (Super Admin only)
  const [expectedMonthlyHours, setExpectedMonthlyHours] = useState('176');

  // Pricing settings
  const [basePrice, setBasePrice] = useState('15');
  const [pricePerEmployee, setPricePerEmployee] = useState('5');
  const [includedEmployees, setIncludedEmployees] = useState('5');

  const handleSendAnnouncement = async () => {
    if (!announcement) {
      toast.error('Please enter an announcement message');
      return;
    }

    setSending(true);
    try {
      await sendSystemAnnouncement({ message: announcement });

      toast.success('Announcement sent to all active tenants!');
      setAnnouncement('');
    } catch (error: any) {
      console.error('Failed to send announcement:', error);
      toast.error(`Failed to send announcement: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleSendNotification = async () => {
    if (!notificationTitle || !notificationBody) {
      toast.error('Please enter title and message');
      return;
    }

    setSending(true);
    try {
      await sendAdminPushNotification({ title: notificationTitle, body: notificationBody });

      toast.success('Push notification sent!');
      setNotificationTitle('');
      setNotificationBody('');
    } catch (error: any) {
      console.error('Failed to send notification:', error);
      toast.error(`Failed: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleSaveExpectedHours = async () => {
    const hours = parseFloat(expectedMonthlyHours);
    if (isNaN(hours) || hours < 80 || hours > 300) {
      toast.error('Expected monthly hours must be between 80 and 300');
      return;
    }

    try {
      const result = await updateGlobalSettings({ expectedMonthlyHours });

      toast.success(result.data.message);
    } catch (error: any) {
      console.error('Failed to save expected hours:', error);
      toast.error(`Failed: ${error.message}`);
    }
  };

  const handleSavePricing = async () => {
    try {
      await updateSystemPricing({
        basePrice: Number(basePrice),
        pricePerEmployee: Number(pricePerEmployee),
        includedEmployees: Number(includedEmployees)
      });

      toast.success('Pricing settings saved!');
    } catch (error: any) {
      console.error('Failed to save pricing:', error);
      toast.error(`Failed: ${error.message}`);
    }
  };

  return (
    <Layout title="System Settings">

      <div className="p-4 space-y-6">
        {/* PRD v4.1: Expected Monthly Hours Configuration (Super Admin Only) */}
        <Card title="⏰ Expected Monthly Hours (Overtime Calculation)">
          <div className="space-y-4">
            <div className="p-4 bg-[#0D9488] bg-opacity-10 rounded-lg mb-4">
              <p className="text-sm text-[#0D9488] font-semibold mb-2">
                📋 PRD v4.1 Requirement:
              </p>
              <p className="text-sm text-[#D4AF37]">
                Only Super Admin can configure expectedMonthlyHours. This value is used for overtime calculations
                for salaried employees. Default: 176 hours (40hrs/week × 4.4 weeks).
              </p>
            </div>

            <Input
              label="Expected Monthly Hours (Default for All Tenants)"
              type="number"
              min="80"
              max="300"
              value={expectedMonthlyHours}
              onChange={(e) => setExpectedMonthlyHours(e.target.value)}
              placeholder="176"
            />

            <div className="p-3 bg-[#3B82F6] bg-opacity-10 border border-[#3B82F6] rounded-lg">
              <p className="text-sm text-[#0D9488] font-semibold mb-1">
                Overtime Formula:
              </p>
              <p className="text-xs text-[#D4AF37] font-mono">
                overtimeHours = max(0, actualHours - {expectedMonthlyHours})
                <br />
                overtimePay = overtimeHours × (monthlySalary / {expectedMonthlyHours}) × 1.5
              </p>
            </div>

            <Button onClick={handleSaveExpectedHours} className="w-full">
              ✓ Save Expected Hours Setting
            </Button>
          </div>
        </Card>

        {/* System Stats */}
        <Card title="📊 System Statistics">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-[#D4AF37] bg-opacity-10 rounded-lg">
              <div className="text-3xl mb-2">🏢</div>
              <p className="text-2xl font-bold text-[#0D9488]">24</p>
              <p className="text-xs text-[#D4AF37]">Total Tenants</p>
            </div>

            <div className="text-center p-4 bg-[#D4AF37] bg-opacity-10 rounded-lg">
              <div className="text-3xl mb-2">👥</div>
              <p className="text-2xl font-bold text-[#0D9488]">342</p>
              <p className="text-xs text-[#D4AF37]">Total Users</p>
            </div>

            <div className="text-center p-4 bg-[#D4AF37] bg-opacity-10 rounded-lg">
              <div className="text-3xl mb-2">📍</div>
              <p className="text-2xl font-bold text-[#0D9488]">58</p>
              <p className="text-xs text-[#D4AF37]">Total Locations</p>
            </div>

            <div className="text-center p-4 bg-[#D4AF37] bg-opacity-10 rounded-lg">
              <div className="text-3xl mb-2">💰</div>
              <p className="text-2xl font-bold text-[#0D9488]">2,450</p>
              <p className="text-xs text-[#D4AF37]">Monthly Revenue (JD)</p>
            </div>
          </div>
        </Card>

        {/* Pricing Settings */}
        <Card title="💰 Pricing Configuration">
          <div className="space-y-4">
            <Input
              label="Base Price per Location (JD)"
              type="number"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              placeholder="15"
            />

            <Input
              label="Included Employees"
              type="number"
              value={includedEmployees}
              onChange={(e) => setIncludedEmployees(e.target.value)}
              placeholder="5"
            />

            <Input
              label="Price per Additional Employee (JD)"
              type="number"
              value={pricePerEmployee}
              onChange={(e) => setPricePerEmployee(e.target.value)}
              placeholder="5"
            />

            <div className="p-4 bg-[#3B82F6] bg-opacity-10 rounded-lg">
              <p className="text-sm text-[#0D9488] font-semibold mb-2">
                Current Pricing Model:
              </p>
              <p className="text-sm text-[#D4AF37]">
                {basePrice} JD per location (includes {includedEmployees} employees)
                <br />
                + {pricePerEmployee} JD per additional employee
                <br />
                30-day free trial for all new tenants
              </p>
            </div>

            <Button onClick={handleSavePricing} className="w-full">
              ✓ Save Pricing Settings
            </Button>
          </div>
        </Card>

        {/* System Announcements */}
        <Card title="📢 Send Announcement">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#0D9488] mb-2">
                Announcement Message
              </label>
              <textarea
                value={announcement}
                onChange={(e) => setAnnouncement(e.target.value)}
                rows={4}
                placeholder="Enter your announcement message for all active tenants..."
                className="w-full px-4 py-3 border-2 border-[#D4AF37] rounded-lg text-[#0D9488] focus:border-[#0D9488] focus:outline-none resize-none"
              />
            </div>

            <div className="p-3 bg-[#F59E0B] bg-opacity-10 border border-[#F59E0B] rounded-lg">
              <p className="text-sm text-[#0D9488]">
                ⚠️ This will send an announcement to all active tenant owners
              </p>
            </div>

            <Button
              onClick={handleSendAnnouncement}
              disabled={sending}
              className="w-full"
            >
              {sending ? '⏳ Sending...' : '📢 Send Announcement'}
            </Button>
          </div>
        </Card>

        {/* Push Notifications */}
        <Card title="🔔 Send Push Notification">
          <div className="space-y-4">
            <Input
              label="Notification Title"
              value={notificationTitle}
              onChange={(e) => setNotificationTitle(e.target.value)}
              placeholder="e.g., System Maintenance"
            />

            <div>
              <label className="block text-sm font-semibold text-[#0D9488] mb-2">
                Notification Body
              </label>
              <textarea
                value={notificationBody}
                onChange={(e) => setNotificationBody(e.target.value)}
                rows={3}
                placeholder="Enter notification message..."
                className="w-full px-4 py-3 border-2 border-[#D4AF37] rounded-lg text-[#0D9488] focus:border-[#0D9488] focus:outline-none resize-none"
              />
            </div>

            <div className="p-3 bg-[#3B82F6] bg-opacity-10 rounded-lg">
              <p className="text-sm text-[#0D9488]">
                ℹ️ Notification will be sent to all users with push enabled
              </p>
            </div>

            <Button
              onClick={handleSendNotification}
              disabled={sending}
              variant="secondary"
              className="w-full"
            >
              {sending ? '⏳ Sending...' : '🔔 Send Push Notification'}
            </Button>
          </div>
        </Card>

        {/* System Actions */}
        <Card title="⚙️ System Actions">
          <div className="space-y-3">
            <Button
              onClick={async () => {
                try {
                  toast.loading('Preparing export...');
                  // TODO: Implement export Cloud Function
                  await new Promise(resolve => setTimeout(resolve, 1500));
                  toast.dismiss();
                  toast.success('Export started! Download will begin shortly.');
                } catch (error) {
                  toast.dismiss();
                  toast.error('Export failed');
                }
              }}
              variant="secondary"
              className="w-full"
            >
              📥 Export All System Data
            </Button>

            <Button
              onClick={() => {
                toast('System logs feature coming soon!');
                // TODO: Navigate to logs viewer or open modal
              }}
              variant="secondary"
              className="w-full"
            >
              📋 View System Logs
            </Button>

            <Button
              onClick={async () => {
                try {
                  toast.loading('Creating backup...');
                  // TODO: Implement backup Cloud Function
                  await new Promise(resolve => setTimeout(resolve, 2000));
                  toast.dismiss();
                  toast.success('Database backup created successfully!');
                } catch (error) {
                  toast.dismiss();
                  toast.error('Backup failed');
                }
              }}
              variant="secondary"
              className="w-full"
            >
              💾 Create Database Backup
            </Button>

            <Button
              onClick={async () => {
                if (confirm('This will clear all cached data. Continue?')) {
                  try {
                    toast.loading('Clearing cache...');
                    // Clear local storage cache
                    await new Promise(resolve => setTimeout(resolve, 800));
                    toast.dismiss();
                    toast.success('Cache cleared successfully!');
                  } catch (error) {
                    toast.dismiss();
                    toast.error('Failed to clear cache');
                  }
                }
              }}
              variant="destructive"
              className="w-full"
            >
              🗑️ Clear System Cache
            </Button>
          </div>
        </Card>

        {/* Activity Stats */}
        <Card title="📈 Activity Statistics (Last 30 Days)">
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 border-b border-[#D4AF37]">
              <span className="text-sm font-semibold text-[#0D9488]">
                New Tenant Signups
              </span>
              <span className="text-lg font-bold text-[#0D9488]">8</span>
            </div>

            <div className="flex justify-between items-center p-3 border-b border-[#D4AF37]">
              <span className="text-sm font-semibold text-[#0D9488]">
                Trial Conversions
              </span>
              <span className="text-lg font-bold text-[#0D9488]">6 (75%)</span>
            </div>

            <div className="flex justify-between items-center p-3 border-b border-[#D4AF37]">
              <span className="text-sm font-semibold text-[#0D9488]">
                Total Clock-Ins
              </span>
              <span className="text-lg font-bold text-[#0D9488]">12,456</span>
            </div>

            <div className="flex justify-between items-center p-3 border-b border-[#D4AF37]">
              <span className="text-sm font-semibold text-[#0D9488]">
                Active Users (Daily)
              </span>
              <span className="text-lg font-bold text-[#0D9488]">285</span>
            </div>

            <div className="flex justify-between items-center p-3">
              <span className="text-sm font-semibold text-[#0D9488]">
                System Uptime
              </span>
              <span className="text-lg font-bold text-[#16A34A]">99.8%</span>
            </div>
          </div>
        </Card>

        {/* Version Info */}
        <Card>
          <div className="text-center">
            <p className="text-sm text-[#D4AF37] mb-1">VireoHR System</p>
            <p className="text-lg font-bold text-[#0D9488]">Version 1.0.0</p>
            <p className="text-xs text-[#D4AF37] mt-2">
              Last Updated: November 2024
            </p>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
